import React, { useState, useEffect } from 'react';
import { Shield, Clock, Phone, CheckCircle } from 'lucide-react';
import { bailAgentService } from '../../../services/bailAgentService';

// Component for displaying current bail agent's credentials and information
const AgentCredentials = () => {
  const [currentAgent, setCurrentAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the currently active bail agent from the database
    const fetchCurrentAgent = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await bailAgentService?.getCurrentAgent();
        
        if (error) {
          // Fallback to default agent data if fetch fails
          setCurrentAgent({
            full_name: 'Damian Sailors',
            license_number: '1N13041',
            phone: '(916) 256-3468',
            bio: 'Licensed California Bail Agent available 24/7',
            specializations: ['Emergency Bail', '24/7 Service'],
            profile_photo_url: null,
            shift_started_at: new Date()?.toISOString()
          });
        } else {
          setCurrentAgent(data);
        }
      } catch (error) {
        // Fallback to default agent data
        setCurrentAgent({
          full_name: 'Damian Sailors',
          license_number: '1N13041',
          phone: '(916) 256-3468',
          bio: 'Licensed California Bail Agent available 24/7',
          specializations: ['Emergency Bail', '24/7 Service'],
          profile_photo_url: null,
          shift_started_at: new Date()?.toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentAgent();
    
    // Set up real-time updates for agent changes
    const interval = setInterval(fetchCurrentAgent, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center space-x-4 p-4 bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200">
        <div className="w-14 h-14 bg-slate-200 rounded-full animate-pulse"></div>
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded animate-pulse mb-2"></div>
          <div className="h-3 bg-slate-200 rounded animate-pulse w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!currentAgent) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <p className="text-red-600 text-sm">Unable to load agent information. Please try again.</p>
      </div>
    );
  }

  const formatShiftTime = (shiftStarted) => {
    if (!shiftStarted) return '';
    const start = new Date(shiftStarted);
    const now = new Date();
    const diffHours = Math.floor((now - start) / (1000 * 60 * 60));
    const diffMins = Math.floor((now - start) / (1000 * 60)) % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  return (
    <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-200/60 rounded-xl p-5 shadow-sm">
      <div className="flex items-center space-x-5">
        {/* Streamlined Agent Photo */}
        <div className="flex-shrink-0">
          {currentAgent?.profile_photo_url ? (
            <img
              src={currentAgent?.profile_photo_url}
              alt={currentAgent?.full_name}
              className="w-14 h-14 rounded-full border-3 border-white shadow-md object-cover"
            />
          ) : (
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full border-3 border-white shadow-md flex items-center justify-center">
              <span className="text-white text-lg font-bold">
                {currentAgent?.full_name?.split(' ')?.map(n => n?.[0])?.join('')}
              </span>
            </div>
          )}
        </div>

        {/* Compact Agent Information */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-slate-900 truncate">
              {currentAgent?.full_name}
            </h2>
            <div className="flex items-center space-x-2 bg-emerald-100 px-3 py-1 rounded-full">
              <CheckCircle className="w-3 h-3 text-emerald-600" />
              <span className="text-emerald-700 text-xs font-medium">Available</span>
            </div>
          </div>

          {/* Essential Information Only */}
          <div className="flex items-center space-x-6 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-blue-600" />
              <span>License: {currentAgent?.license_number}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{currentAgent?.phone}</span>
            </div>
            {currentAgent?.shift_started_at && (
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700 font-medium">
                  On duty {formatShiftTime(currentAgent?.shift_started_at)}
                </span>
              </div>
            )}
          </div>

          {/* Simplified Bio */}
          {currentAgent?.bio && (
            <p className="text-slate-600 mt-2 text-sm leading-relaxed line-clamp-1">
              {currentAgent?.bio}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentCredentials;